import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  BENCHMARKS, defaultIndexForCcy,
  calcEstimatedCfInr, totalLcPricingInr,
  fmtCr, fmtL, fmtDate, INDEX_BADGE_COLORS, resolveSpot
} from '../utils/importFinancingUtils';

export default function LCActionModal({ lc, mode: initialMode, fxRates, interestRates, onClose, onSaved }) {
  const [mode, setMode] = useState(initialMode); // 'settle' | 'bc' | 'both'
  const [settling, setSettling] = useState(false);
  const [savingBc, setSavingBc] = useState(false);

  const cf = calcEstimatedCfInr(lc, fxRates, interestRates);
 const pricing = totalLcPricingInr(lc, fxRates);

  // ── Settle form state ──────────────────────────────────────
  const [settleForm, setSettleForm] = useState({
    settled_date: new Date().toISOString().split('T')[0],
    settlement_rate_inr: lc.spot_rate_inr || '',
    actual_outflow_inr: Math.round(cf),
    bank_reference: '',
  });

  // ── BC form state ──────────────────────────────────────────
  const bm = BENCHMARKS[lc.currency] || { options: ['Fixed'], label: 'Fixed' };
  const defaultIndex = defaultIndexForCcy(lc.currency);

  const [bcForm, setBcForm] = useState({
    bc_ref: '',
    lender_bank: '',
    amount_fcy: lc.amount_fcy,
    tenor_days: lc.lc_type === 'Usance' && lc.usance_period_days ? lc.usance_period_days : 90,
    index_type: defaultIndex,
    index_rate: '',
    spread_rate: '',
    all_in_rate: '',
    drawdown_date: new Date().toISOString().split('T')[0],
    repayment_date: '',
    hedge_status: 'Unhedged',
    hedged_amount_fcy: 0,
    contracted_rate_inr: '',
  });

  // Auto-calc all-in when index or spread changes
useEffect(() => {
  const idx = parseFloat(bcForm.index_rate) || 0;
  const spd = parseFloat(bcForm.spread_rate) || 0;
  if (bcForm.index_type === 'Fixed') {
    // Fixed rate — all-in is the fixed rate itself, spread not applicable
    if (idx > 0) setBcForm(f => ({ ...f, all_in_rate: idx.toFixed(2), spread_rate: '' }));
  } else {
    // Floating — all-in = index + spread
    if (idx > 0 || spd > 0) setBcForm(f => ({ ...f, all_in_rate: (idx + spd).toFixed(2) }));
  }
}, [bcForm.index_rate, bcForm.spread_rate, bcForm.index_type]);

  // Auto-calc repayment date from drawdown + tenor
  useEffect(() => {
    if (bcForm.drawdown_date && bcForm.tenor_days) {
      const d = new Date(bcForm.drawdown_date);
      d.setDate(d.getDate() + parseInt(bcForm.tenor_days));
      setBcForm(f => ({ ...f, repayment_date: d.toISOString().split('T')[0] }));
    }
  }, [bcForm.drawdown_date, bcForm.tenor_days]);

  // Estimated BC interest cost
 const estBcInterest = bcForm.all_in_rate && bcForm.tenor_days
  ? lc.amount_fcy * resolveSpot(lc.currency, fxRates, lc.spot_rate_inr) * (parseFloat(bcForm.all_in_rate) / 100) * (parseInt(bcForm.tenor_days) / 365)
  : 0;

  async function handleSettle() {
    setSettling(true);
    await supabase.from('import_financing').update({
      status: 'Settled',
      settled_date: settleForm.settled_date,
      settlement_rate_inr: settleForm.settlement_rate_inr,
      actual_outflow_inr: settleForm.actual_outflow_inr,
      bank_reference: settleForm.bank_reference,
    }).eq('id', lc.id);
    setSettling(false);
    onSaved();
  }

  async function handleAvailBC() {
    setSavingBc(true);
    // 1. Insert BC record
    await supabase.from('import_financing_bc').insert({
      company_id: lc.company_id,
      lc_id: lc.id,
      lc_ref: lc.lc_ref,
      bc_ref: bcForm.bc_ref,
      lender_bank: bcForm.lender_bank,
      currency: lc.currency,
      amount_fcy: bcForm.amount_fcy,
      drawdown_date: bcForm.drawdown_date,
      repayment_date: bcForm.repayment_date,
      tenor_days: bcForm.tenor_days,
      index_type: bcForm.index_type,
      index_rate: bcForm.index_rate,
      spread_rate: bcForm.spread_rate,
      all_in_rate: bcForm.all_in_rate,
      spot_rate_inr: lc.spot_rate_inr,
      hedge_status: bcForm.hedge_status,
      hedged_amount_fcy: bcForm.hedged_amount_fcy,
      contracted_rate_inr: bcForm.contracted_rate_inr || null,
    });
    // 2. Update LC status
    await supabase.from('import_financing').update({ status: 'Converted to BC' }).eq('id', lc.id);
    setSavingBc(false);
    onSaved();
  }

  const indexColor = INDEX_BADGE_COLORS[bcForm.index_type] || INDEX_BADGE_COLORS.Fixed;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-[560px] max-h-[88vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-white">{lc.lc_ref} — Maturity Action</h2>
            <p className="text-xs text-gray-400 mt-0.5">{lc.beneficiary} · {lc.currency} {(lc.amount_fcy/1000).toFixed(0)}K</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl">✕</button>
        </div>

        {/* Info strip */}
        <div className="flex gap-4 flex-wrap px-4 py-3 bg-gray-800 rounded-xl mb-4 text-xs">
          {[
            { label: 'Maturity', value: fmtDate(lc.maturity_date) },
            { label: 'Est. INR Outflow', value: fmtCr(cf) },
            { label: 'Total LC Cost', value: fmtL(pricing) },
            { label: 'Hedge', value: lc.hedge_status },
          ].map(i => (
            <div key={i.label}>
              <div className="text-gray-500">{i.label}</div>
              <div className="font-medium text-white mt-0.5">{i.value}</div>
            </div>
          ))}
        </div>

        {/* Mode tabs (shown when 'both') */}
        {initialMode === 'both' && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setMode('settle')}
              className={`flex-1 py-2 text-sm rounded-lg border transition-colors ${mode === 'settle' ? 'bg-teal-700 border-teal-600 text-white' : 'border-gray-700 text-gray-400 hover:text-gray-200'}`}
            >
              ✓ Settle LC
            </button>
            <button
              onClick={() => setMode('bc')}
              className={`flex-1 py-2 text-sm rounded-lg border transition-colors ${mode === 'bc' ? 'bg-purple-800 border-purple-600 text-white' : 'border-gray-700 text-gray-400 hover:text-gray-200'}`}
            >
              ⇄ Avail Buyers Credit
            </button>
          </div>
        )}

        {/* ── SETTLE FORM ── */}
        {(mode === 'settle' || (mode !== 'bc' && initialMode === 'settle')) && (
          <div className="border border-gray-700 rounded-xl p-4 mb-4">
            <div className="text-sm font-medium text-teal-400 mb-3 flex items-center gap-2">
              <span>✓</span> Settle LC
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Settlement Date', key: 'settled_date', type: 'date' },
                { label: 'Settlement Rate (INR)', key: 'settlement_rate_inr', type: 'number', placeholder: lc.spot_rate_inr },
                { label: 'Actual INR Outflow', key: 'actual_outflow_inr', type: 'number' },
                { label: 'Bank Reference No.', key: 'bank_reference', type: 'text', placeholder: 'HDFC/2026/001234' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs text-gray-400 block mb-1">{f.label}</label>
                  <input
                    type={f.type}
                    value={settleForm[f.key]}
                    placeholder={f.placeholder}
                    onChange={e => setSettleForm(s => ({ ...s, [f.key]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={handleSettle}
                disabled={settling}
                className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-500 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                {settling ? 'Saving...' : 'Confirm Settlement'}
              </button>
            </div>
          </div>
        )}

        {/* ── BC FORM ── */}
        {(mode === 'bc' || initialMode === 'bc') && (
          <div className="border border-purple-900 rounded-xl p-4">
            <div className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
              <span>⇄</span> Avail Buyers Credit
            </div>

            {/* Benchmark info banner */}
            <div className="flex items-center gap-2 px-3 py-2 bg-purple-900/30 border border-purple-800 rounded-lg mb-3 text-xs">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${indexColor}`}>{bcForm.index_type}</span>
              <span className="text-gray-400">Indicative {bm.label} for {lc.currency}</span>
              <span className="text-white font-medium ml-auto">
                {bm.options[0] !== 'Fixed' ? 'See dashboard for rates' : 'Fixed rate — enter below'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">BC Reference</label>
                <input type="text" placeholder="BC-2024-004" value={bcForm.bc_ref}
                  onChange={e => setBcForm(f=>({...f, bc_ref: e.target.value}))}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Lender Bank</label>
                <input type="text" placeholder="e.g. DBS Bank" value={bcForm.lender_bank}
                  onChange={e => setBcForm(f=>({...f, lender_bank: e.target.value}))}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">BC Amount (FCY)</label>
                <input type="number" value={bcForm.amount_fcy}
                  onChange={e => setBcForm(f=>({...f, amount_fcy: e.target.value}))}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Tenor (days)</label>
                <input type="number" value={bcForm.tenor_days}
                  onChange={e => setBcForm(f=>({...f, tenor_days: e.target.value}))}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </div>

              {/* Index — currency-locked */}
              <div>
                <label className="text-xs text-gray-400 block mb-1">
                  Index <span className="text-gray-500">({lc.currency}-linked)</span>
                </label>
               <select value={bcForm.index_type}
  onChange={e => {
    const selected = e.target.value;
    const rateCodeMap = { 'SOFR': 'FED_FUNDS', '€STR': 'ECB_DEPOSIT', 'SONIA': 'BOE_BASE', 'SORA': 'MAS_SORA' };
    const rateCode = rateCodeMap[selected];
    const overnightRate = rateCode && interestRates[rateCode]
      ? interestRates[rateCode].overnightRate
      : '';
    setBcForm(f => ({ ...f, index_type: selected, index_rate: selected === 'Fixed' ? '' : overnightRate }));
  }}
  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white">
  {bm.options.map(o => <option key={o}>{o}</option>)}
</select>
              </div>
           <div>
  <label className="text-xs text-gray-400 block mb-1">
    Benchmark Rate (%)
    {bcForm.index_type !== 'Fixed' && (
      <span className="text-teal-500 ml-1">— Indicative</span>
    )}
  </label>
  <input type="number" step="0.01"
    value={bcForm.index_rate}
    placeholder={bcForm.index_type === 'Fixed' ? 'Enter fixed rate' : 'Loading...'}
    disabled={bcForm.index_type !== 'Fixed'}
    onChange={e => setBcForm(f=>({...f, index_rate: e.target.value}))}
    className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-60" />
</div>

              <div>
  <label className="text-xs text-gray-400 block mb-1">
    Spread (%) {bcForm.index_type === 'Fixed' && <span className="text-gray-600">— not applicable</span>}
  </label>
  <input type="number" step="0.01" placeholder="0.15" value={bcForm.spread_rate}
    disabled={bcForm.index_type === 'Fixed'}
    onChange={e => setBcForm(f=>({...f, spread_rate: e.target.value}))}
    className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-40" />
</div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">All-in Cost (%)</label>
                <input type="number" step="0.01" value={bcForm.all_in_rate}
                  onChange={e => setBcForm(f=>({...f, all_in_rate: e.target.value}))}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Drawdown Date</label>
                <input type="date" value={bcForm.drawdown_date}
                  onChange={e => setBcForm(f=>({...f, drawdown_date: e.target.value}))}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Repayment Date</label>
                <input type="date" value={bcForm.repayment_date}
                  onChange={e => setBcForm(f=>({...f, repayment_date: e.target.value}))}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Hedge Status</label>
                <select value={bcForm.hedge_status}
                  onChange={e => setBcForm(f=>({...f, hedge_status: e.target.value}))}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white">
                  {['Unhedged','Partially Hedged','Fully Hedged'].map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Contracted Hedge Rate</label>
                <input type="number" step="0.01" placeholder={lc.spot_rate_inr}
                  value={bcForm.contracted_rate_inr}
                  onChange={e => setBcForm(f=>({...f, contracted_rate_inr: e.target.value}))}
                  disabled={bcForm.hedge_status === 'Unhedged'}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-40" />
              </div>
            </div>

            {/* Estimated cost callout */}
            {estBcInterest > 0 && (
              <div className="mt-3 px-3 py-2 bg-gray-800 rounded-lg text-xs text-gray-400">
                Estimated BC interest cost at {bcForm.all_in_rate}% for {bcForm.tenor_days} days:&nbsp;
                <span className="text-white font-medium">{fmtL(estBcInterest)}</span>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={handleAvailBC}
                disabled={savingBc || !bcForm.bc_ref || !bcForm.lender_bank || !bcForm.all_in_rate}
                className="px-4 py-2 text-sm bg-purple-700 hover:bg-purple-600 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                {savingBc ? 'Saving...' : 'Create Buyers Credit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
